import IdentityProvider from './IdentityProvider';
import {IIdentityProviderCredentials} from '../IIdentityProvider';

interface IFacebookAuthResponse {
	authResponse: {
		accessToken: string;
	};
	status: string;
}

declare const FB: {
	init: (config: Record<string, unknown>) => void;
	getLoginStatus: (callback: (response: IFacebookAuthResponse) => void) => void;
	login: (callback: (response: IFacebookAuthResponse) => void, options: Record<string, unknown>) => void;
};

/**
 * Represents FacebookIdentityProvider
 */
class FacebookIdentityProvider extends IdentityProvider {
	private FB: typeof FB;

	/**
	 * SDK Initialization
	 * @returns {Promise} promise resolved when the loading process ends
	*/
	initSdk() {
		return new Promise<FacebookIdentityProvider>((resolve) => {
			if (this.FB) {
				resolve(this);
			}

			(window as Window & {fbAsyncInit?: () => void}).fbAsyncInit = () => {
				FB.init({
					appId: this.config.appId,
					cookie: true,
					xfbml: true,
					version: 'v16.0'
				});

				this.FB = FB;
				resolve(this);
			};
		});
	}

	/**
	 * Authenticate and sign in the user
	 * @returns {Promise} promise resolved when the auth process ends
	 */
	protected auth(): Promise<IFacebookAuthResponse> {
		return new Promise((resolve, reject) => {
			if (!this.FB) {
				reject();
			}

			this.FB.getLoginStatus((response: IFacebookAuthResponse) => {
				if (response.status === 'connected') {
					resolve(response);
				} else {
					this.FB.login((loginResponse: IFacebookAuthResponse) => {
						if (loginResponse.status === 'connected') {
							resolve(loginResponse);
						} else {
							reject();
						}
					}, {
						scope: 'public_profile, email',
					});
				}
			});
		});
	}

	/**
	 * Return the provider credentials
	 * @returns {Promise<IIdentityProviderCredentials>|null} object containing credentials
	 */
	async getProviderCredentials(): Promise<IIdentityProviderCredentials> {
		try {
			const response = await this.auth();

			return {
				'providerId': 'facebook',
				'providerData': {
					'accessToken': response.authResponse.accessToken,
				},
			};
		} catch (e) {
			return null;
		}
	}
}

export default new FacebookIdentityProvider();